import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { Brand } from '../components/Brand';
import { ThemeToggle } from '../components/ThemeToggle';

/**
 * Deliberately says the same thing whether or not the address has an account. Telling somebody
 * "no account found" turns this form into a way of discovering who does have one.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that. Try again shortly.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-wrap">
      <section className="login-pitch">
        <Brand large />
        <div className="login-pitch-body">
          <div className="eyebrow">Lucentra Systems · Egen Holdings</div>
          <h1>
            Locked out? <em>Let us help</em>.
          </h1>
          <p className="subtitle" style={{ marginTop: 20, maxWidth: '46ch' }}>
            We will email a link that lets you choose a new password. It works once and expires
            within the hour.
          </p>
        </div>
        <p className="login-pitch-foot">Access is granted by an administrator. There is no public sign-up.</p>
      </section>

      <section className="login-form-side">
        <div className="login-card">
          <div className="row-between mb-2">
            <div className="eyebrow" style={{ margin: 0 }}>
              Forgotten password
            </div>
            <ThemeToggle compact />
          </div>

          {sent ? (
            <>
              <h2 style={{ fontSize: 24, marginBottom: 6 }}>Check your email</h2>
              <p className="subtitle">
                If <strong>{email}</strong> has an account, a link is on its way. It expires in an
                hour and can be used once.
              </p>
              <div className="callout callout-ok mt-2">
                Nothing has changed yet. Your current password still works until you choose a new
                one.
              </div>
              <div className="row mt-2">
                <Link to="/login" className="btn btn-primary">
                  Back to sign in
                </Link>
                <button type="button" className="btn btn-quiet" onClick={() => setSent(false)}>
                  Use a different address
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: 24, marginBottom: 6 }}>Reset your password</h2>
              <p className="subtitle mb-2">The work address your account was created with.</p>

              {error && (
                <div className="callout" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={submit} noValidate>
                <label className="field">
                  <span className="field-label">Email address</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    placeholder="you@lucentra.co.za"
                    required
                    autoFocus
                  />
                </label>

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  disabled={busy || !email.trim()}
                >
                  {busy ? 'Sending…' : 'Email me a link'}
                </button>
              </form>

              <p className="login-note">
                <Link to="/login" style={{ textDecoration: 'underline' }}>
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
